import { CHANGE_EVENT, clearList, readList, removeItem } from '@theme/saved-products';

/**
 * <s-saved-products list="wishlist|compare"> — header entry point for a saved
 * product list. Keeps [data-saved-count] in sync and renders the list inside
 * the nested dialog's [data-saved-items]. The dialog itself opens through the
 * native show-modal command on the header button.
 *
 * Labels (from Liquid translations): data-remove-label, data-price-label,
 * data-category-label, data-view-label.
 */
class SSavedProducts extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    window.addEventListener(CHANGE_EVENT, (event) => {
      if (event.detail?.list === this.list) this.#render();
    }, opts);
    window.addEventListener('storage', () => this.#render(), opts);

    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const remove = target?.closest('[data-remove-id]');
      if (remove) {
        removeItem(this.list, remove.getAttribute('data-remove-id'));
        return;
      }
      if (target?.closest('[data-saved-clear]')) clearList(this.list);
    }, opts);

    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get list() {
    return this.getAttribute('list') || 'wishlist';
  }

  #render() {
    const items = readList(this.list);
    const count = this.querySelector('[data-saved-count]');
    if (count) {
      count.textContent = String(items.length);
      count.hidden = items.length === 0;
    }

    const container = this.querySelector('[data-saved-items]');
    const empty = this.querySelector('[data-saved-empty]');
    const clear = this.querySelector('[data-saved-clear]');
    if (!container) return;

    container.replaceChildren();
    if (empty) empty.hidden = items.length > 0;
    if (clear) clear.hidden = items.length === 0;
    if (items.length === 0) return;

    container.append(this.list === 'compare' ? this.#compareTable(items) : this.#wishlist(items));
  }

  #el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  #image(item, className) {
    const wrap = this.#el('div', className);
    if (item.image) {
      const img = this.#el('img', 'size-full object-contain');
      img.src = item.image;
      img.alt = item.title || '';
      img.loading = 'lazy';
      wrap.append(img);
    }
    return wrap;
  }

  #removeButton(item, className) {
    const button = this.#el('button', className, this.dataset.removeLabel || '×');
    button.type = 'button';
    button.setAttribute('data-remove-id', String(item.id));
    return button;
  }

  #wishlist(items) {
    const ul = this.#el('ul', 'flex flex-col divide-y divide-border');
    ul.setAttribute('role', 'list');
    for (const item of items) {
      const li = this.#el('li', 'flex items-center gap-4 py-4');
      li.append(this.#image(item, 'size-20 shrink-0 overflow-hidden rounded-xl bg-white border border-border'));

      const info = this.#el('div', 'flex min-w-0 flex-1 flex-col gap-1');
      const link = this.#el('a', 'font-semibold text-on-surface line-clamp-2 hover:text-interactive', item.title);
      link.href = item.url;
      info.append(link);
      if (item.category) info.append(this.#el('span', 'text-sm text-on-surface-secondary', item.category));
      info.append(this.#el('span', 'font-semibold text-interactive', item.price));
      li.append(info);

      li.append(this.#removeButton(item, 'shrink-0 text-sm font-medium text-on-surface-secondary underline cursor-pointer hover:text-on-surface'));
      ul.append(li);
    }
    return ul;
  }

  #compareTable(items) {
    const wrap = this.#el('div', 'overflow-x-auto');
    const table = this.#el('table', 'w-full min-w-[560px] border-collapse text-sm');
    const tbody = this.#el('tbody');

    const rows = [
      { label: '', cell: (item) => {
        const td = this.#el('td', 'p-3 align-top');
        td.append(this.#image(item, 'aspect-square w-full overflow-hidden rounded-xl bg-white border border-border'));
        const link = this.#el('a', 'mt-3 block font-semibold text-on-surface line-clamp-3 hover:text-interactive', item.title);
        link.href = item.url;
        td.append(link);
        return td;
      } },
      { label: this.dataset.priceLabel, cell: (item) => this.#el('td', 'p-3 font-semibold text-interactive', item.price) },
      { label: this.dataset.categoryLabel, cell: (item) => this.#el('td', 'p-3 text-on-surface', item.category || '–') },
      { label: '', cell: (item) => {
        const td = this.#el('td', 'p-3');
        const view = this.#el('a', 'inline-flex h-10 items-center rounded-full bg-interactive px-4 font-semibold text-interactive-text hover:opacity-90', this.dataset.viewLabel);
        view.href = item.url;
        td.append(view, this.#removeButton(item, 'ms-3 text-sm font-medium text-on-surface-secondary underline cursor-pointer hover:text-on-surface'));
        return td;
      } },
    ];

    for (const row of rows) {
      const tr = this.#el('tr', 'border-b border-border last:border-0');
      tr.append(this.#el('th', 'w-28 p-3 text-start align-top font-semibold text-on-surface-secondary', row.label || ''));
      for (const item of items) tr.append(row.cell(item));
      tbody.append(tr);
    }

    table.append(tbody);
    wrap.append(table);
    return wrap;
  }
}

if (!customElements.get('s-saved-products')) customElements.define('s-saved-products', SSavedProducts);
